<template>
    <div class="quiz-corporate">
        <activity-generating-placeholder
            v-if="isGenerationStatusUiEnabled && (isGenerating || isFailedGeneration)"
            activity-type="quiz"
            :generation-status="generationStatus"
            :generation-error="generationError"
        />

        <div v-else-if="uuid && !isLoading" class="quiz-corporate__body">
            <!-- Meta bar. -->
            <section class="quiz-meta-card">
                <span class="quiz-meta-card__title">{{ title }}</span>
                <div class="quiz-meta">
                    <span class="quiz-meta__item">
                        <e-icon :icon="noTimeLimit ? 'stopwatch-slash' : 'stopwatch'" />
                        <span>{{ timeLimitLabel }}</span>
                    </span>
                    <span class="quiz-meta__item">
                        <e-icon icon="check-circle" />
                        <span>Total Questions: {{ started ? total : questionCount }}</span>
                    </span>
                    <span v-if="content.lockdown" class="quiz-meta__item">
                        <e-icon icon="lock" />
                        <span>Navigation: Locked</span>
                    </span>
                </div>
            </section>

            <!-- Question (after Begin). -->
            <section v-if="started && !inReview && currentQuestion" class="qqc-card">
                <div class="qqc-progress" aria-hidden="true">
                    <span
                        v-for="n in total"
                        :key="n"
                        class="qqc-progress__seg"
                        :class="{ 'qqc-progress__seg--done': n <= index + 1 }"
                    />
                </div>

                <div class="qqc-head">
                    <span class="qqc-head__num">Question #{{ index + 1 }}</span>
                    <span class="qqc-pill">
                        {{
                            isFreeForm(currentQuestion)
                                ? 'Free Response'
                                : isMultipleAnswer(currentQuestion)
                                    ? 'Multiple Answers'
                                    : 'Single Answer'
                        }}
                    </span>
                </div>

                <display-content
                    v-if="currentQuestion.question.body"
                    class="qqc-question"
                    :content="currentQuestion.question.body"
                />

                <!-- Free-form response. -->
                <textarea
                    v-if="isFreeForm(currentQuestion)"
                    class="qqc-freeform"
                    rows="6"
                    :value="freeFormText(currentQuestion.id)"
                    @input="setFreeForm(currentQuestion, $event.target.value)"
                    @blur="persistDraft(currentQuestion)"
                />

                <!-- Choice answers. -->
                <div v-else class="qqc-options">
                    <button
                        v-for="answer in currentQuestion.question.answers"
                        :key="answer.id"
                        type="button"
                        class="qqc-option"
                        :class="{ 'qqc-option--selected': isSelected(currentQuestion, answer.id) }"
                        @click="select(currentQuestion, answer.id)"
                    >
                        <span
                            class="qqc-option__radio"
                            :class="{
                                'qqc-option__radio--on': isSelected(currentQuestion, answer.id),
                                'qqc-option__radio--square': isMultipleAnswer(currentQuestion),
                            }"
                        />
                        <display-content v-if="answer.body" class="qqc-option__body" :content="answer.body" />
                    </button>

                    <p
                        v-if="!currentQuestion.question.answers || !currentQuestion.question.answers.length"
                        class="qqc-empty"
                    >
                        No answers to display.
                    </p>
                </div>

                <div class="qqc-nav">
                    <e-button v-if="!isFirst" icon-left="angle-left" @click="prev"> Previous </e-button>
                    <span v-else />

                    <e-button v-if="!isLast" icon-right="angle-right" type="is-primary" @click="next"> Next </e-button>

                    <e-button
                        v-else
                        type="is-primary"
                        :loading="isSubmitting"
                        :disabled="isSubmitting"
                        @click="submitQuiz"
                    >
                        {{ type === 'survey' ? 'Submit Survey' : 'Submit Quiz' }}
                    </e-button>
                </div>
            </section>

            <!-- Started but the quiz has no questions. -->
            <section v-else-if="started && !inReview" class="qqc-card">
                <p class="qqc-empty">This quiz has no questions to display.</p>
            </section>

            <!-- Results (after submit). -->
            <section v-else-if="inReview" class="qqc-card quiz-results">
                <div v-if="showCorrect" class="quiz-results__row">
                    <span class="quiz-results__label">Correct:</span>
                    <span class="quiz-results__value">{{ correctCount }}/{{ reviewTotal }}</span>
                </div>
                <div v-if="scorePercent != null" class="quiz-results__row">
                    <span class="quiz-results__label">Score:</span>
                    <span class="quiz-results__value">{{ scorePercent }}%</span>
                </div>
                <p v-if="!score && !showCorrect" class="qqc-empty">Your response has been submitted.</p>
            </section>

            <!-- Start screen (before Begin): a Begin button. -->
            <section v-else class="qqc-card quiz-start">
                <e-button
                    v-if="!readOnly"
                    type="is-primary"
                    size="is-medium"
                    :loading="isLaunching"
                    :disabled="isLaunching"
                    @click="launchQuiz"
                >
                    {{ beginLabel }}
                </e-button>

                <p v-if="hasError" class="quiz-start__error">Unable to load this quiz. Please try again.</p>

                <div v-if="blockedByAttempt" class="quiz-start__blocked">
                    <p>
                        You're in the middle of "{{ blockedByAttempt.title }}". Please finish that quiz before
                        starting this one.
                    </p>
                    <router-link
                        v-if="blockedActivityRoute"
                        :to="blockedActivityRoute"
                        class="quiz-start__blocked-link"
                    >
                        Go to "{{ blockedByAttempt.title }}"
                    </router-link>
                </div>
            </section>

            <!-- Past result (completed attempt, attempts still remaining). -->
            <section v-if="!started && hasPastResult" class="qqc-card quiz-results">
                <p class="quiz-results__heading">
                    Last attempt<span v-if="submittedLabel"> · submitted {{ submittedLabel }}</span>
                </p>

                <div v-if="showCorrect" class="quiz-results__row">
                    <span class="quiz-results__label">Correct:</span>
                    <span class="quiz-results__value">{{ correctCount }}/{{ reviewTotal }}</span>
                </div>
                <div v-if="scorePercent != null" class="quiz-results__row">
                    <span class="quiz-results__label">Score:</span>
                    <span class="quiz-results__value">{{ scorePercent }}%</span>
                </div>
            </section>

            <!-- After submitting, offer an immediate retake (Begin New Attempt) when attempts
                 remain. Next / "Complete Course" is provided universally by the corporate shell
                 (ActivityPageCorporate.vue), so we no longer render the shared completion row here. -->
            <div
                v-if="inReview && canRetake && !readOnly"
                class="quiz-corporate__retake tw:mt-4 tw:mb-4 tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-4"
            >
                <e-button type="is-primary" :loading="isLaunching" :disabled="isLaunching" @click="launchQuiz">
                    {{ beginLabel }}
                </e-button>
            </div>
        </div>

        <e-loading v-if="isLoading" v-model:active="isLoading" is-full-page />
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';

import useQuizActivity from '@/composables/useQuizActivity.js';
import ActivityStatus from '@/models/ActivityStatus.js';

import Quiz from './Quiz.vue';

/**
 * Corporate Quiz — taken INLINE on the activity page (Figma 17111:49485).
 *
 * Thin presentational glue: it `extends Quiz.vue` purely for the activity METADATA
 * (title, content, quizId, questionCount, generation status, submissions, …) and
 * delegates the entire take-and-submit flow to the `useQuizActivity` composable
 * (create attempt → load questions → save answers → submit → score). No confirmation
 * modal, no `/quiz` route, no QuizAttempt store. Stanbridge Quiz.vue is untouched.
 */

export default {
    name: 'QuizCorporate',

    extends: Quiz,

    // extends does NOT inherit the parent's setup() — re-provide translate, and mount
    // the quiz-taking engine here (its reactive surface is exposed to the template).
    setup() {
        const { t: translate } = useI18n();
        return { translate, ...useQuizActivity() };
    },

    computed: {
        quizTimeLimitSeconds() {
            const remediation = this.head(this.remediations);
            if (remediation?.quiz_time_limit) {
                return remediation.quiz_time_limit;
            }
            if (this.userHasAuthorizedAttempt && this.userUnusedAuthorizedAttempts[0]?.quiz_time_limit) {
                return this.userUnusedAuthorizedAttempts[0].quiz_time_limit;
            }
            return this.content?.time_limit ?? null;
        },

        noTimeLimit() {
            return this.quizTimeLimitSeconds == null;
        },

        timeLimitLabel() {
            return this.noTimeLimit ? 'No Time Limit' : `Time Limit: ${Math.ceil(this.quizTimeLimitSeconds / 60)} min`;
        },

        // Corporate always gets full review (the backend bypasses the show_*_in_review
        // toggles for corporate), so always show the Correct row once we have questions.
        showCorrect() {
            return this.reviewTotal > 0;
        },

        submittedLabel() {
            return this.submittedAt ? this.$moment.utc(this.submittedAt).local().format('MMM D, YYYY · h:mm A') : null;
        },

        // Deep link to the activity whose attempt is blocking this one.
        blockedActivityRoute() {
            if (!this.blockedByAttempt) return null;
            return `/courses/${this.blockedByAttempt.course_id}/activities/${this.blockedByAttempt.activity_id}`;
        },

        // "Begin New Attempt" once a prior attempt exists; first-time label otherwise.
        beginLabel() {
            if (this.hasPastResult) {
                return this.type === 'survey' ? 'Begin New Survey' : 'Begin New Attempt';
            }
            return this.type === 'survey' ? this.translate('begin_survey') : this.translate('begin_quiz_attempt');
        },

        // Whether the learner still has attempts left to retake (mirrors the resume
        // `allowRetake` rule). Gates the review-state "Begin New Attempt" footer.
        canRetake() {
            return !this.maxReached;
        },
    },

    watch: {
        // Once the activity metadata is loaded, resume an in-progress attempt or show
        // the review for a completed one (runs once; the composable no-ops if idle is
        // already left). Fires again when quizActivityId resolves from the store.
        quizActivityId: { immediate: true, handler: 'maybeResume' },
    },

    methods: {
        // Check for a prior attempt on this activity and resume/review it.
        maybeResume() {
            if (this.readOnly || this._resumeStarted || !this.quizActivityId) return;
            this._resumeStarted = true;
            this.resume({
                quizActivityId: this.quizActivityId,
                userId: this.$store.getters['Auth/userId'],
                type: this.type,
                // Attempts remaining → land on Begin (past result shown below); else review.
                allowRetake: !this.maxReached,
            });
        },

        // Begin: create the attempt + load its questions (composable owns the rest).
        launchQuiz() {
            return this.start({
                quizVersionId: this.quizId,
                quizActivityId: this.quizActivityId,
                userId: this.$store.getters['Auth/userId'],
                type: this.type,
                preview: false,
            }).catch(() => {}); // error surfaced via the composable's `hasError`
        },

        async submitQuiz() {
            try {
                await this.submit({
                    userId: this.$store.getters['Auth/userId'],
                    quizActivityId: this.quizActivityId,
                    endTime: this.$moment.utc().format('YYYY-MM-DD HH:mm:ss'),
                    // Corporate clients always allow review — always fetch the score. The
                    // composable's /review fetch is best-effort (403 → plain submitted view).
                    reviewAllowed: true,
                });
            } catch {
                return; // error surfaced via the composable's `hasError`
            }

            await this.refreshCompletionStatus();
        },

        // Re-sync activity completion after submit so the shell chrome (completion
        // banner, prev/next, sidebar check) updates live without a page reload. The
        // shell's isCompleted reads the API-appended Activity.completed, so we must
        // re-fetch the activity (not just verifyActivity, which only sets softCompleted).
        async refreshCompletionStatus() {
            try {
                await this.fetchActivityData();
                await this.fetchActualAttemptCount();
                if (this.computedActivity) {
                    ActivityStatus.syncFromActivity(this.computedActivity);
                }
                await this.verifyActivity();
            } catch (error) {
                // Non-fatal: review still renders; chrome syncs on next load.
                console.error('Failed to refresh quiz completion status:', error);
            }
        },
    },
};
</script>

<style scoped lang="scss">
.quiz-corporate {
    // The corporate shell section owns the 860px content measure; fill it.
    min-width: 0;

    // Breathing room beneath the quiz content, before the shell's Next footer — matching the
    // spacing the other corporate renderers give (quiz content is short, so without this the
    // footer sits jammed against the last card).
    margin-bottom: 2.5rem;

    .quiz-corporate__body {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .quiz-corporate__admin {
        display: flex;
        justify-content: flex-end;
    }

    // Meta bar card (Figma 17111:49491).
    .quiz-meta-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.75rem 1.5rem;
        padding: 18px 21.6px;
        background: #fff;
        border: 1px solid #e5eff5;
        border-radius: 7.2px;
        box-shadow: 0 4px 4px rgba(2, 5, 10, 0.04);
    }

    .quiz-meta-card__title {
        color: #363636;
        font-weight: 700;
        font-size: 1.0125rem;
    }

    .quiz-meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.5rem 1.575rem;
    }

    .quiz-meta__item {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        color: #363636;
        font-size: 0.9rem;
        white-space: nowrap;
    }

    .quiz-divider {
        margin: 0 0 1rem;
        border: 0;
        border-top: 1px solid #e5eff5;
    }

    // Start screen card: centered description + Begin button.
    .quiz-start {
        align-items: center;
        text-align: center;
        gap: 1.5rem;
        padding-top: 2.75rem;
        padding-bottom: 2.75rem;
    }

    .quiz-start__error {
        color: #b91c1c;
        font-size: 0.85rem;
    }

    .quiz-start__blocked {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        max-width: 32rem;
        padding: 14.4px 18px;
        border: 1px solid #fde68a;
        border-radius: 7.2px;
        background: #fffbeb;
        color: #92400e;
        font-size: 0.875rem;
    }

    .quiz-start__blocked-link {
        font-weight: 700;
        color: var(--primary-color, #3b82f6);
    }

    // Question card (Figma 17111:49528).
    .qqc-card {
        display: flex;
        flex-direction: column;
        gap: 1.575rem;
        padding: 19.195px 21.6px;
        background: #fff;
        border: 1px solid #e5eff5;
        border-radius: 7.2px;
        box-shadow: 0 4px 4px rgba(2, 5, 10, 0.04);
    }

    .qqc-progress {
        display: flex;
        gap: 4px;
    }

    .qqc-progress__seg {
        flex: 1 0 0;
        min-width: 0;
        height: 6px;
        border-radius: 3px;
        background: #e2e8f0;

        &--done {
            background: var(--primary-color, #3b82f6);
        }
    }

    .qqc-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
    }

    .qqc-head__num {
        color: #363636;
        font-weight: 700;
        font-size: 1.0125rem;
    }

    .qqc-pill {
        flex-shrink: 0;
        padding: 3.5px 7px;
        border-radius: 6px;
        background: #e0f2fe;
        color: #0369a1;
        font-weight: 700;
        font-size: 0.766rem;
        white-space: nowrap;
    }

    .qqc-question {
        color: #4a4a4a;
        font-weight: 700;
        font-size: 1.0125rem;
        line-height: 1.3;

        :deep(p) {
            margin: 0;
        }
    }

    .qqc-options {
        display: flex;
        flex-direction: column;
        gap: 0.675rem;
    }

    .qqc-option {
        display: flex;
        align-items: center;
        gap: 0.675rem;
        width: 100%;
        padding: 14.4px 18px;
        border: 1px solid #dfe7ef;
        border-radius: 7.2px;
        background: #fff;
        box-shadow: 0 4px 4px rgba(2, 5, 10, 0.04);
        text-align: left;
        cursor: pointer;
        transition:
            border-color 0.15s ease,
            background-color 0.15s ease;

        &:hover {
            border-color: #cbd5e1;
            background: #f8fafc;
        }

        &--selected {
            border-color: var(--primary-color, #3b82f6);
            background: #eff6ff;
        }
    }

    .qqc-option__radio {
        flex-shrink: 0;
        width: 21.6px;
        height: 21.6px;
        border: 2px solid #cbd5e1;
        border-radius: 50%;
        position: relative;

        // Square indicator for multiple-answer questions.
        &--square {
            border-radius: 6px;
        }

        &--on {
            border-color: var(--primary-color, #3b82f6);

            &::after {
                content: '';
                position: absolute;
                inset: 3px;
                border-radius: 50%;
                background: var(--primary-color, #3b82f6);
            }
        }

        &--square.qqc-option__radio--on::after {
            border-radius: 3px;
        }
    }

    .qqc-freeform {
        width: 100%;
        padding: 14.4px 18px;
        border: 1px solid #dfe7ef;
        border-radius: 7.2px;
        background: #fff;
        color: #4a4a4a;
        font: inherit;
        line-height: 1.45;
        resize: vertical;

        &:focus {
            outline: none;
            border-color: var(--primary-color, #3b82f6);
        }
    }

    // Completed banner (review).
    .quiz-completed-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 7.2px 10.8px;
        background: rgba(240, 253, 244, 0.95);
        border: 1px solid #16a34a;
        border-radius: 7.2px;
        box-shadow: 0 4px 8px rgba(1, 8, 4, 0.04);
    }

    .quiz-completed-banner__left {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        color: #16a34a;
        font-size: 0.875rem;
    }

    .quiz-completed-banner__meta {
        color: #4a4a4a;
        font-weight: 600;
        font-size: 0.75rem;
        white-space: nowrap;
    }

    // Results card (review).
    .quiz-results {
        gap: 0.5rem;
    }

    .quiz-results__heading {
        margin-bottom: 0.25rem;
        color: #363636;
        font-weight: 700;
        font-size: 1.0125rem;
    }

    .quiz-results__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
    }

    .quiz-results__label {
        color: #363636;
        font-weight: 700;
        font-size: 0.9rem;
    }

    .quiz-results__value {
        color: #4a4a4a;
        font-size: 0.9rem;
    }

    .qqc-option__body {
        color: #4a4a4a;
        font-size: 0.9rem;
        line-height: 1.45;

        :deep(p) {
            margin: 0;
        }
    }

    .qqc-empty {
        color: #94a3b8;
    }

    .qqc-nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
}
</style>
